import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription, from } from 'rxjs';
import { ContactForm } from 'src/app/classes/form-groups/contact-form';
import {
  ClientRegisteredVendor,
  CompanyVendor,
  Contract,
  ExemptVendor,
  Vendor,
  VendorContact
} from 'src/app/classes/models';
import { SubNationalDivision } from 'src/app/classes/models/sub-national-division';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { LocalizationInfo } from 'src/app/interfaces/localization-info';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { CompanyService } from 'src/app/services/company.service';
import { ContractService } from 'src/app/services/contract.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { SubNationalDivisionService } from 'src/app/services/sub-national-division.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { companiesService } from 'src/app/shared/vive-api/companies.service';

@Component({
  selector: 'app-contract-vendor-selection',
  templateUrl: './contract-vendor-selection.component.html',
  styleUrls: ['./contract-vendor-selection.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ContractVendorSelectionComponent implements OnDestroy {
  constructor(
    private ToastService: ToastService,
    private AuthService: AuthService,
    private UtilityService: UtilityService,
    private CompanyService: CompanyService,
    private CompanyVendorService: CompanyVendorService,
    private SubNationalDivisionService: SubNationalDivisionService,
    private SharedDataService: SharedDataService,
    private ContractService: ContractService,
    private route: ActivatedRoute
  ) {
    this.vendorContactForm.removeControl('MailingAddress');
    // We don't want to copy UserId when creating a contract contact from existing vendor
    this.vendorContactForm.controls.UserId.disable();
    this.vendorContactForm.controls.StreetAddress?.controls.isStreet.setValue(
      true
    );

    this.loadContractVendor();

    from(
      companiesService.findOneById({
        pathParams: { companyId: this.AuthService.getUserCompanyId() }
      })
    ).subscribe({
      next: (Company) => {
        if (!Company) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No Company Record Found.',
            { returnError: true }
          );
        }

        const { country } = Company;

        if (country) {
          this.localizationInfo =
            this.UtilityService.getLocalizedValuesMap(country);

          this.SubNationalDivisionService.getSubNationalDivisions({
            country: country === 'US' ? 'USA' : 'Canada'
          }).subscribe({
            next: (data: SubNationalDivision[]) => {
              this.states = data;
            }
          });
        }
      }
    });

    this.sharedDataServiceSubscription =
      this.SharedDataService.listenForEvent().subscribe({
        next: ({ eventName, data }) => {
          if (eventName === 'isFormValid') {
            const errors = this.UtilityService.generateValidationErrorsObject(
              this.vendorContactForm
            );
            if (errors) {
              throw this.ToastService.addErrorToast(
                'Error',
                'All required fields must be completed to proceed.',
                { returnError: true }
              );
            } else {
              return this.SharedDataService.sendEvent('formIsValid');
            }
          }
        }
      });

    this.vendorContactForm.valueChanges.subscribe({
      next: (changes) => {
        if (changes) {
          Object.assign(changes, {
            ContractId: this.ContractId,
            isContractVendor: true
          });

          const newContract = new Contract({});

          Object.assign(newContract, {
            id: this.ContractId,
            VendorContact: changes
          });

          this.SharedDataService.sendEvent('UpdateContract', newContract);
        }
      }
    });
  }

  vendorContactForm: ContactForm = new ContactForm();

  localizationInfo!: LocalizationInfo;
  states!: SubNationalDivision[];
  selectedVendor: any | null = null;
  vendorContact?: VendorContact;
  ContractId!: number;
  sharedDataServiceSubscription!: Subscription;
  selectedRowsPrimaryKeys: Set<string | number> = new Set();
  phoneMask = this.UtilityService.phoneMask;

  loadContractVendor() {
    const paramMap = this.route.snapshot.paramMap;
    const ContractId = Number(paramMap.get('ContractId'));

    this.ContractId = ContractId;

    this.ContractService.getContract({
      id: ContractId,
      Vendor: JSON.stringify({}),
      ClientRegisteredVendor: JSON.stringify({}),
      ExemptVendor: JSON.stringify({}),
      VendorContact: JSON.stringify({
        StreetAddress: {}
      })
    }).subscribe({
      next: (Contract: Contract) => {
        if (!Contract) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No Contract Record Found.',
            { returnError: true }
          );
        }

        const { VendorContact, Vendor, ClientRegisteredVendor, ExemptVendor } =
          Contract;
        const companyVendorQuery = {
          attributes: JSON.stringify(['id'])
        };

        if (VendorContact) {
          this.vendorContact = VendorContact;
          this.loadVendorContact();
        }

        if (Vendor) {
          this.selectedVendor = Vendor;
          Object.assign(companyVendorQuery, { VendorId: Vendor.id });
        }

        if (ExemptVendor) {
          this.selectedVendor = ExemptVendor;
          Object.assign(companyVendorQuery, {
            ExemptVendorId: ExemptVendor.id
          });
        }

        if (ClientRegisteredVendor) {
          this.selectedVendor = ClientRegisteredVendor;
          Object.assign(companyVendorQuery, {
            ClientRegisteredVendorId: ClientRegisteredVendor.id
          });
        }

        const { id } = this.selectedVendor;

        if (id) {
          this.CompanyVendorService.getCompanyVendor(
            companyVendorQuery
          ).subscribe({
            next: (CompanyVendor: CompanyVendor) => {
              if (CompanyVendor) {
                const { id } = CompanyVendor;

                if (id) {
                  this.selectedRowsPrimaryKeys.add(id);
                }
              }
            }
          });
        }
      }
    });
  }

  loadVendorContact() {
    if (this.vendorContact) {
      this.vendorContactForm.patchValue(this.vendorContact);
    }
  }

  actionEvent({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: any;
  }) {
    const selectedRow = rowData;
    const companyVendorQuery = {};
    const vendorQuery = {
      Contact: {
        StreetAddress: {}
      }
    };

    this.selectedVendor = selectedRow;

    if (!this.selectedVendor) {
      throw this.ToastService.addErrorToast('Error', 'No Selected Vendor.', {
        returnError: true
      });
    }

    const { id, ClientRegisteredVendorId, VendorId, ExemptVendorId } =
      this.selectedVendor;

    if (ClientRegisteredVendorId) {
      Object.assign(companyVendorQuery, {
        ClientRegisteredVendorId,
        ClientRegisteredVendor: JSON.stringify(vendorQuery)
      });
    }

    if (VendorId) {
      Object.assign(companyVendorQuery, {
        VendorId,
        Vendor: JSON.stringify(vendorQuery)
      });
    }

    if (ExemptVendorId) {
      Object.assign(companyVendorQuery, {
        ExemptVendorId,
        ExemptVendor: JSON.stringify(vendorQuery)
      });
    }

    this.CompanyVendorService.getCompanyVendor(companyVendorQuery).subscribe({
      next: (CompanyVendor: CompanyVendor) => {
        if (!CompanyVendor) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No CompanyVendor Record Found.',
            { returnError: true }
          );
        }

        let currentVendor:
          Vendor | ClientRegisteredVendor | ExemptVendor | null | undefined;

        const { Vendor, ClientRegisteredVendor, ExemptVendor } = CompanyVendor;

        if (VendorId) {
          currentVendor = Vendor;
        }

        if (ClientRegisteredVendorId) {
          currentVendor = ClientRegisteredVendor;
        }

        if (ExemptVendorId) {
          currentVendor = ExemptVendor;
        }

        if (!currentVendor) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No Vendor Record Found.',
            { returnError: true }
          );
        }

        this.SharedDataService.sendEvent(
          'UpdateContract',
          new Contract({
            id: this.ContractId,
            VendorId,
            ClientRegisteredVendorId,
            ExemptVendorId
          })
        );

        const { Contacts } = currentVendor;

        if (Contacts && Contacts.length > 0) {
          const primaryContact = Contacts.find(
            (contact) => contact.isPrimaryCompliance
          );
          const firstContact = Contacts.length > 0 ? Contacts[0] : null;

          if (primaryContact) {
            this.vendorContactForm.patchValue(primaryContact);
          }

          if (!primaryContact && firstContact) {
            this.vendorContactForm.patchValue(firstContact);
          }
        } else {
          this.vendorContactForm.reset();
        }

        this.vendorContactForm.controls.id?.disable();
        this.vendorContactForm.controls.StreetAddress?.controls.id?.disable();
      }
    });
  }

  ngOnDestroy(): void {
    this.sharedDataServiceSubscription.unsubscribe();
  }
}
